/**
 * A storage chunk: 65,536 consecutive rows of one column.
 *
 * The chunk is the unit of allocation, transfer, and dirty tracking. Allocation
 * is lazy twice over (Requirement 1.2 vs 14.6):
 *
 *  - an *absent* chunk means "all 65,536 rows are empty" and costs nothing, so
 *    a 5,000,000-row sheet with one written cell allocates one chunk;
 *  - within a chunk, each payload array is allocated on first use, so a
 *    text-only chunk never allocates the `Float64Array`.
 *
 * Payload layout. `kinds` tags every local row (see the `KIND_*` constants).
 * Numeric values live in `numbers`; dictionary codes, boolean flags, and error
 * ordinals live in `textCodes`. When a chunk that already holds codes takes its
 * first numeric write, the codes are folded into `numbers` and `textCodes` is
 * dropped, so the two arrays are never allocated at the same time. That keeps
 * the worst case at 1 + 8 + 4 = 13 bytes per allocated cell, which is exactly
 * the budget the memory model of Requirement 14.6 is built on.
 *
 * Styles use a "uniform style" fast path: while every row in the chunk shares
 * `uniformStyleId`, no `styleIds` array exists at all. The array appears only on
 * the first write of a differing style id, which is the uncommon case.
 *
 * Formulas and error metadata are rare relative to values, so they live in
 * sparse maps rather than parallel arrays.
 */

import { CHUNK_ROWS, ERROR_CODES, type Addr, type CellValue, type ErrorCode } from '../types.js';

/** Kind tag stored in `Chunk.kinds`. */
export const KIND_EMPTY = 0;
export const KIND_NUMBER = 1;
export const KIND_TEXT = 2;
export const KIND_BOOL = 3;
export const KIND_ERROR = 4;

/** Number of distinct kind tags, for count vectors. */
export const KIND_COUNT = 5;

export type CellKind =
  | typeof KIND_EMPTY
  | typeof KIND_NUMBER
  | typeof KIND_TEXT
  | typeof KIND_BOOL
  | typeof KIND_ERROR;

/** Style id 0 is the workbook default style; it is never stored explicitly. */
export const DEFAULT_STYLE_ID = 0;

/**
 * Upper bound on typed-array bytes per allocated cell: 1 (`kinds`) +
 * 8 (`numbers`) + 4 (`styleIds`). Requirement 14.6 / Property 59.
 */
export const MAX_BYTES_PER_CELL = 13;

/** Fixed bookkeeping charged per allocated chunk (object header, map slots). */
export const CHUNK_OVERHEAD_BYTES = 256;

/** Accounted overhead of one sparse-map entry (formula or error metadata). */
export const SPARSE_ENTRY_OVERHEAD_BYTES = 64;

/**
 * A formula cell. `ast` is left untyped until the Formula_Parser lands; the
 * cached value is what the Data_Store serves to readers between recalculations.
 */
export interface FormulaCell {
  source: string;
  ast?: unknown;
  cached: CellValue;
}

/** Optional error provenance, kept sparsely (Requirement 16.4). */
export interface ErrorDetail {
  detail?: string;
  origin?: Addr;
}

export interface Chunk {
  /** One kind tag per local row. Always allocated. */
  kinds: Uint8Array;
  /** Numeric values, and folded codes once allocated. Lazy. */
  numbers?: Float64Array;
  /** Dictionary codes, boolean flags, and error ordinals. Lazy, and absent whenever `numbers` exists. */
  textCodes?: Int32Array;
  /** Per-row style ids. Absent while the chunk is uniformly styled. */
  styleIds?: Int32Array;
  /** The style of every row for which `styleIds` is absent. */
  uniformStyleId: number;
  /** Sparse formula cells, keyed by local row. */
  formulas?: Map<number, FormulaCell>;
  /** Sparse error provenance, keyed by local row. */
  errorDetails?: Map<number, ErrorDetail>;
  /** Number of local rows whose kind is not `KIND_EMPTY`. */
  nonEmpty: number;
}

/** Per-chunk memory accounting (Requirement 14.6). */
export interface ChunkMemory {
  /** Rows the chunk makes addressable, allocated or not. */
  allocatedCells: number;
  /** Typed-array bytes; bounded by {@link MAX_BYTES_PER_CELL} per allocated cell. */
  cellBytes: number;
  /** Fixed per-chunk bookkeeping. */
  overheadBytes: number;
  /** Sparse formula and error-metadata bytes. */
  sparseBytes: number;
}

/** Chunk index that holds `row`. */
export function chunkIndexOfRow(row: number): number {
  return Math.floor(row / CHUNK_ROWS);
}

/** Row offset of `row` inside its chunk. */
export function localRowOf(row: number): number {
  return row - chunkIndexOfRow(row) * CHUNK_ROWS;
}

/** First absolute row of `chunkIndex`. */
export function firstRowOfChunk(chunkIndex: number): number {
  return chunkIndex * CHUNK_ROWS;
}

export function createChunk(uniformStyleId: number = DEFAULT_STYLE_ID): Chunk {
  return { kinds: new Uint8Array(CHUNK_ROWS), uniformStyleId, nonEmpty: 0 };
}

/** Ordinal used to store an error code in a numeric payload slot. */
export function errorOrdinal(code: ErrorCode): number {
  return ERROR_CODES.indexOf(code);
}

/** Inverse of {@link errorOrdinal}. Unknown ordinals decode to `#REF!`. */
export function errorFromOrdinal(ordinal: number): ErrorCode {
  return ERROR_CODES[ordinal] ?? '#REF!';
}

function checkLocalRow(local: number): void {
  if (!Number.isInteger(local) || local < 0 || local >= CHUNK_ROWS) {
    throw new RangeError(`local row out of range: ${local}`);
  }
}

export function getKind(chunk: Chunk, local: number): CellKind {
  checkLocalRow(local);
  return (chunk.kinds[local] ?? KIND_EMPTY) as CellKind;
}

/** Reads the payload slot of `local`, whichever array currently backs it. */
export function getPayload(chunk: Chunk, local: number): number {
  checkLocalRow(local);
  if (chunk.numbers !== undefined) return chunk.numbers[local] ?? 0;
  if (chunk.textCodes !== undefined) return chunk.textCodes[local] ?? 0;
  return 0;
}

/**
 * Allocates the `Float64Array` payload, folding any existing integer codes into
 * it so the two payload arrays never coexist.
 */
function ensureNumbers(chunk: Chunk): Float64Array {
  const existing = chunk.numbers;
  if (existing !== undefined) return existing;
  const numbers = new Float64Array(CHUNK_ROWS);
  const codes = chunk.textCodes;
  if (codes !== undefined) {
    numbers.set(codes);
    delete chunk.textCodes;
  }
  chunk.numbers = numbers;
  return numbers;
}

/** Returns the array that should hold an integer payload for this chunk. */
function ensureCodes(chunk: Chunk): Float64Array | Int32Array {
  if (chunk.numbers !== undefined) return chunk.numbers;
  const existing = chunk.textCodes;
  if (existing !== undefined) return existing;
  const codes = new Int32Array(CHUNK_ROWS);
  chunk.textCodes = codes;
  return codes;
}

function writeKind(chunk: Chunk, local: number, kind: CellKind): void {
  const previous = chunk.kinds[local] ?? KIND_EMPTY;
  if (previous === KIND_EMPTY && kind !== KIND_EMPTY) chunk.nonEmpty += 1;
  else if (previous !== KIND_EMPTY && kind === KIND_EMPTY) chunk.nonEmpty -= 1;
  chunk.kinds[local] = kind;
  if (previous === KIND_ERROR && kind !== KIND_ERROR) clearErrorDetail(chunk, local);
}

export function setNumber(chunk: Chunk, local: number, value: number): void {
  checkLocalRow(local);
  ensureNumbers(chunk)[local] = value;
  writeKind(chunk, local, KIND_NUMBER);
}

export function setTextCode(chunk: Chunk, local: number, code: number): void {
  checkLocalRow(local);
  ensureCodes(chunk)[local] = code;
  writeKind(chunk, local, KIND_TEXT);
}

export function setBool(chunk: Chunk, local: number, value: boolean): void {
  checkLocalRow(local);
  ensureCodes(chunk)[local] = value ? 1 : 0;
  writeKind(chunk, local, KIND_BOOL);
}

export function setError(
  chunk: Chunk,
  local: number,
  code: ErrorCode,
  detail?: ErrorDetail,
): void {
  checkLocalRow(local);
  ensureCodes(chunk)[local] = errorOrdinal(code);
  writeKind(chunk, local, KIND_ERROR);
  if (detail !== undefined && (detail.detail !== undefined || detail.origin !== undefined)) {
    let map = chunk.errorDetails;
    if (map === undefined) {
      map = new Map();
      chunk.errorDetails = map;
    }
    map.set(local, detail);
  }
}

/** Clears the value of `local`, leaving its style untouched (Requirement 2.8). */
export function setEmpty(chunk: Chunk, local: number): void {
  checkLocalRow(local);
  if (chunk.numbers !== undefined) chunk.numbers[local] = 0;
  else if (chunk.textCodes !== undefined) chunk.textCodes[local] = 0;
  writeKind(chunk, local, KIND_EMPTY);
}

export function getStyleId(chunk: Chunk, local: number): number {
  checkLocalRow(local);
  const ids = chunk.styleIds;
  if (ids === undefined) return chunk.uniformStyleId;
  return ids[local] ?? chunk.uniformStyleId;
}

export function setStyleId(chunk: Chunk, local: number, styleId: number): void {
  checkLocalRow(local);
  let ids = chunk.styleIds;
  if (ids === undefined) {
    if (styleId === chunk.uniformStyleId) return;
    ids = new Int32Array(CHUNK_ROWS);
    if (chunk.uniformStyleId !== 0) ids.fill(chunk.uniformStyleId);
    chunk.styleIds = ids;
  }
  ids[local] = styleId;
}

/** Bulk style application: restores the uniform-style fast path. */
export function fillStyleId(chunk: Chunk, styleId: number): void {
  delete chunk.styleIds;
  chunk.uniformStyleId = styleId;
}

/** Collapses `styleIds` back to the uniform fast path when all rows agree. */
export function compactStyleIds(chunk: Chunk): boolean {
  const ids = chunk.styleIds;
  if (ids === undefined) return false;
  const first = ids[0] ?? DEFAULT_STYLE_ID;
  for (let i = 1; i < ids.length; i++) {
    if (ids[i] !== first) return false;
  }
  fillStyleId(chunk, first);
  return true;
}

export function getErrorDetail(chunk: Chunk, local: number): ErrorDetail | undefined {
  return chunk.errorDetails?.get(local);
}

function clearErrorDetail(chunk: Chunk, local: number): void {
  const map = chunk.errorDetails;
  if (map === undefined) return;
  map.delete(local);
  if (map.size === 0) delete chunk.errorDetails;
}

export function getFormula(chunk: Chunk, local: number): FormulaCell | undefined {
  return chunk.formulas?.get(local);
}

export function setFormula(chunk: Chunk, local: number, cell: FormulaCell): void {
  checkLocalRow(local);
  let map = chunk.formulas;
  if (map === undefined) {
    map = new Map();
    chunk.formulas = map;
  }
  map.set(local, cell);
}

export function clearFormula(chunk: Chunk, local: number): void {
  const map = chunk.formulas;
  if (map === undefined) return;
  map.delete(local);
  if (map.size === 0) delete chunk.formulas;
}

/** True when the chunk holds no values and no formulas. */
export function isChunkEmpty(chunk: Chunk): boolean {
  return chunk.nonEmpty === 0 && (chunk.formulas === undefined || chunk.formulas.size === 0);
}

/**
 * True when dropping the chunk from its column loses nothing: no values, no
 * formulas, no per-row styles, and no non-default uniform style.
 */
export function isChunkDisposable(chunk: Chunk): boolean {
  return (
    isChunkEmpty(chunk) &&
    chunk.styleIds === undefined &&
    chunk.uniformStyleId === DEFAULT_STYLE_ID &&
    (chunk.errorDetails === undefined || chunk.errorDetails.size === 0)
  );
}

export function chunkMemory(chunk: Chunk): ChunkMemory {
  let cellBytes = chunk.kinds.byteLength;
  if (chunk.numbers !== undefined) cellBytes += chunk.numbers.byteLength;
  if (chunk.textCodes !== undefined) cellBytes += chunk.textCodes.byteLength;
  if (chunk.styleIds !== undefined) cellBytes += chunk.styleIds.byteLength;

  let sparseBytes = 0;
  const formulas = chunk.formulas;
  if (formulas !== undefined) {
    for (const cell of formulas.values()) {
      sparseBytes += SPARSE_ENTRY_OVERHEAD_BYTES + cell.source.length * 2;
    }
  }
  const errors = chunk.errorDetails;
  if (errors !== undefined) {
    for (const detail of errors.values()) {
      sparseBytes += SPARSE_ENTRY_OVERHEAD_BYTES + (detail.detail?.length ?? 0) * 2;
    }
  }

  return {
    allocatedCells: CHUNK_ROWS,
    cellBytes,
    overheadBytes: CHUNK_OVERHEAD_BYTES,
    sparseBytes,
  };
}
